import * as React from "react";
import { useState } from "react";
import { StickyNote } from "lucide-react";
import type { CookSessionApiItem, RecipeApiItem, RecipeStep } from "@recipevault/shared";
import { Button } from "../../ui/button.js";
import { Textarea } from "../../ui/textarea.js";
import { ParallelHintCard } from "./ParallelHintCard.js";
import { StepPhotoChip } from "./StepPhotoChip.js";
import { StepPhotoModal } from "./StepPhotoModal.js";
import { parallelStepIdsOf } from "./parallelism.js";

// rv: Horizontal swipe-peek for step navigation (spec §6.4) is deferred for v1.
// The bottom-bar Next/Back buttons are the primary navigation. Revisit for v2.

/** Shortens step text to ~80 chars for the NEXT preview line. */
function truncate(text: string): string {
  return text.length > 80 ? text.slice(0, 80).trimEnd() + "…" : text;
}

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface StepPaneProps {
  householdId: string;
  recipe: RecipeApiItem;
  /** The current step being displayed. */
  step: RecipeStep;
  /** All steps in the recipe (needed for symmetric parallel resolution). */
  allSteps: RecipeStep[];
  /** Per-step completion state, keyed by step id (for the NEXT preview line). */
  stepStates: CookSessionApiItem["stepStates"];
  /** Called when the user taps a timer pill. */
  onStartTimer: (label: string, minutes: number) => void;
  /** Called when the user submits a note for this step. */
  onAddNote: (text: string) => void;
  /** Called when the user taps "Start now" on a parallel hint card. */
  onStartParallel: (stepId: string) => void;
}

// ---------------------------------------------------------------------------
// StepPane
// ---------------------------------------------------------------------------

/**
 * Central top pane of cooking mode (spec §6.4).
 *
 * Composes:
 * - Step text (plain text — never innerHTML)
 * - Timer pills (one per step.timers entry)
 * - Note button → inline textarea → onAddNote
 * - ParallelHintCard (symmetric: even if this step's parallelWith is empty,
 *   back-references from other steps are resolved)
 * - StepPhotoChip + StepPhotoModal
 * - NEXT preview line (static text: the next not-done, non-prep step)
 */
export function StepPane({
  householdId,
  recipe,
  step,
  allSteps,
  stepStates,
  onStartTimer,
  onAddNote,
  onStartParallel,
}: StepPaneProps) {
  const [noteOpen, setNoteOpen] = useState(false);
  const [noteText, setNoteText] = useState("");
  const [photosOpen, setPhotosOpen] = useState(false);

  // Symmetric parallel resolution: Y is parallel with X if X ∈ Y.parallelWith
  // OR Y ∈ X.parallelWith. parallelStepIdsOf handles both directions.
  const parallel = parallelStepIdsOf(allSteps, step.id)
    .map((id) => allSteps.find((s) => s.id === id))
    .filter((s): s is RecipeStep => !!s);

  // NEXT preview: first not-done, non-prep step after the current one.
  // Mirrors the "next incomplete cook step" predicate in CookingMode.tsx.
  const currentIndex = allSteps.findIndex((s) => s.id === step.id);
  const nextStep =
    currentIndex >= 0
      ? (allSteps
          .slice(currentIndex + 1)
          .find((s) => s.kind !== "prep" && stepStates[s.id]?.status !== "done") ?? null)
      : null;

  function handleSaveNote() {
    const text = noteText.trim();
    if (!text) return;
    onAddNote(text);
    setNoteText("");
    setNoteOpen(false);
  }

  return (
    <div className="flex flex-col gap-4 px-4 py-4">
      {/* ------------------------------------------------------------------ */}
      {/* Step text — rendered as PLAIN TEXT, never innerHTML (spec §6.4)     */}
      {/* ------------------------------------------------------------------ */}
      <p className="text-2xl font-semibold leading-snug text-foreground">
        {step.text}
      </p>

      {/* ------------------------------------------------------------------ */}
      {/* Timer pills — one per step.timers entry                             */}
      {/* ------------------------------------------------------------------ */}
      {step.timers.length > 0 && (
        <div className="flex flex-wrap gap-2" role="group" aria-label="Step timers">
          {step.timers.map((timer, idx) => (
            <button
              key={`${timer.label}-${idx}`}
              type="button"
              onClick={() => onStartTimer(timer.label, timer.minutes)}
              className="inline-flex min-h-[48px] items-center gap-1.5 rounded-full border border-emerald-500/40 bg-emerald-500/15 px-4 py-2 text-base font-medium text-emerald-200 hover:bg-emerald-500/25 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500/50"
              aria-label={`${timer.label} · ${timer.minutes}m`}
            >
              <span>{timer.label}</span>
              <span className="text-emerald-400">·</span>
              <span>{timer.minutes}m</span>
            </button>
          ))}
        </div>
      )}

      {/* ------------------------------------------------------------------ */}
      {/* Parallel hint card                                                  */}
      {/* ------------------------------------------------------------------ */}
      <ParallelHintCard parallelSteps={parallel} onStart={onStartParallel} />

      {/* ------------------------------------------------------------------ */}
      {/* Note button + inline textarea                                        */}
      {/* ------------------------------------------------------------------ */}
      {!noteOpen ? (
        <Button
          type="button"
          variant="outline"
          size="cook"
          onClick={() => setNoteOpen(true)}
          aria-label="Add a note for this step"
          className="self-start gap-2"
        >
          <StickyNote className="size-5" aria-hidden="true" />
          Add note
        </Button>
      ) : (
        <div className="flex flex-col gap-2">
          <Textarea
            value={noteText}
            onChange={(e) => setNoteText(e.target.value)}
            placeholder="Add a note for this step…"
            className="min-h-[80px]"
            aria-label="Step note"
          />
          <div className="flex gap-2">
            <Button
              type="button"
              size="sm"
              onClick={handleSaveNote}
              aria-label="Save note"
            >
              Save note
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => {
                setNoteText("");
                setNoteOpen(false);
              }}
            >
              Cancel
            </Button>
          </div>
        </div>
      )}

      {/* ------------------------------------------------------------------ */}
      {/* Step photo chip + modal                                              */}
      {/* ------------------------------------------------------------------ */}
      <StepPhotoChip
        householdId={householdId}
        recipe={recipe}
        step={step}
        onOpen={() => setPhotosOpen(true)}
      />
      <StepPhotoModal
        householdId={householdId}
        recipe={recipe}
        step={step}
        open={photosOpen}
        onOpenChange={setPhotosOpen}
      />

      {/* ------------------------------------------------------------------ */}
      {/* NEXT preview — static text, no tap target, no state                 */}
      {/* ------------------------------------------------------------------ */}
      {nextStep && (
        <p className="text-sm text-zinc-500">NEXT · {truncate(nextStep.text)}</p>
      )}
    </div>
  );
}
